/* eslint-disable no-alert */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet-async";
import remarkGfm from "remark-gfm";
import Header from "parts/Header";
import Footer from "parts/Footer";
import "./blogDetail.css";
import { fetchAllBlogs, fetchBlogContent } from "../utils/blogLoader.js";

const slugify = (text) => String(text)
  .toLowerCase()
  .replace(/[^\w\s-]/g, "")
  .replace(/\s+/g, "-")
  .replace(/-+/g, "-")
  .trim();

function BlogDetailPage() {
  const { slug } = useParams();
  const [content, setContent] = useState("");
  const [data, setData] = useState(null);
  const [popular, setPopular] = useState([]);
  const [readProgress, setReadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeId, setActiveId] = useState("");
  const [tocItems, setTocItems] = useState([]);
  const articleRef = useRef(null);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      const blogs = await fetchAllBlogs();
      const blogData = blogs.find((b) => b.slug === slug);
      if (!blogData) return;

      const md = await fetchBlogContent(blogData.url);
      const mergedData = { ...blogData, ...md.data };

      setData(mergedData);
      setContent(md.content);

      const headings = [...(md.content.matchAll(/^(#{1,3})\s+(.+)$/gm))].map(
        ([, hashes, text]) => ({
          level: hashes.length,
          text: text.trim(),
          id: slugify(text.trim()),
        }),
      );
      setTocItems(headings);

      setPopular(blogs.filter((b) => b.slug !== slug).slice(0, 3));
      setIsLoading(false);
    }

    load();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;
      const { top, height } = article.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrolled = Math.max(0, -top);
      const total = height - windowHeight;
      const progress = Math.min(100, (scrolled / total) * 100);
      setReadProgress(progress);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [data]);

  useEffect(() => {
    if (!tocItems.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0% -70% 0%", threshold: 0 },
    );
    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [tocItems]);

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="bdr-skeleton-wrapper">
          <div className="bdr-skeleton-hero" />
          <div className="bdr-skeleton-body">
            <div className="bdr-skeleton-line bdr-skeleton-title" />
            <div className="bdr-skeleton-line" />
            <div className="bdr-skeleton-line" />
            <div className="bdr-skeleton-line bdr-skeleton-short" />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!data) return null;

  return (
    <>
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.description} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* Reading Progress Bar */}
      <div className="bdr-progress-bar">
        <div className="bdr-progress-fill" style={{ width: `${readProgress}%` }} />
      </div>

      <Header />

      <main className="bdr-main" ref={articleRef}>

        {/* ── HERO ── */}
        <section className="bdr-hero">

          {/* Background */}
          <div className="bdr-hero-bg">
            {data.image && (
              <img src={data.image} alt={data.title} className="bdr-hero-img" />
            )}
            <div className="bdr-hero-overlay" />
            <div className="bdr-hero-vignette" />
          </div>

          {/* Content Card */}
          <div className="bdr-hero-content">
            <div className="bdr-hero-card">

              <h1 className="bdr-hero-title">{data.title}</h1>

              {data.description && (
                <p className="bdr-hero-description">{data.description}</p>
              )}

              {data.author && (
                <div className="bdr-hero-author">
                  <a href={data.authorLinkedin} target="_blank" rel="noopener noreferrer" className="bdr-author-left bdr-author-link">
                    <div className="bdr-author-avatar">
                      {data.authorImage ? (
                        <img
                          src={data.authorImage}
                          alt={data.author}
                          className="bdr-author-img"
                        />
                      ) : (
                        data.author.charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="bdr-author-name">{data.author}</span>
                  </a>
                  <span className="bdr-hero-date">{data.date}</span>
                </div>
              )}

            </div>
          </div>
        </section>

        {/* ── ARTICLE BODY ── */}
        <div className={`bdr-layout ${tocItems.length > 0 ? "bdr-layout--toc" : ""}`}>

          {/* Left sticky sidebar */}
          <aside className="bdr-sidebar">
            <div className="bdr-sidebar-inner">
              <p className="bdr-sidebar-label">Reading Progress</p>
              <div className="bdr-sidebar-track">
                <div
                  className="bdr-sidebar-fill"
                  style={{ height: `${readProgress}%` }}
                />
              </div>
              <p className="bdr-sidebar-pct">{Math.round(readProgress)}%</p>

              <div className="bdr-sidebar-share">
                <p className="bdr-sidebar-label" style={{ marginTop: "2rem" }}>Share</p>
                <div className="bdr-share-icons">
                  {/* Copy Link */}
                  <button
                    type="button"
                    className="bdr-share-btn"
                    title="Copy link"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert("Link copied!");
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                  </button>

                  {/* LinkedIn Share */}
                  <button
                    type="button"
                    className="bdr-share-btn"
                    title="Share on LinkedIn"
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const title = encodeURIComponent(data?.title || "");
                      const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}`;
                      window.open(shareUrl, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452H17.21v-5.569c0-1.328-.025-3.037-1.851-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.087V9h3.104v1.561h.045c.433-.82 1.492-1.685 3.07-1.685 3.285 0 3.89 2.163 3.89 4.977v6.599zM5.337 7.433a1.804 1.804 0 110-3.609 1.804 1.804 0 010 3.609zM6.806 20.452H3.867V9h2.939v11.452zM22.225 0H1.771C.792 0 0 .771 0 1.723v20.549C0 23.229.792 24 1.771 24h20.451C23.2 24 24 23.229 24 22.271V1.723C24 .771 23.2 0 22.222 0h.003z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <article className="bdr-article">
            <div className="bdr-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
                  h3: ({ children }) => <h3 id={slugify(String(children))}>{children}</h3>,
                  h4: ({ children }) => <h4 id={slugify(String(children))}>{children}</h4>,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            {/* Author card at bottom */}
            {data.author && (
              <div className="bdr-author-card">
                <div className="bdr-author-avatar">
                  {data.authorImage ? (
                    <img src={data.authorImage} alt={data.author} className="bdr-author-img" />
                  ) : (
                    data.author.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="bdr-author-card-body">
                  <p className="bdr-author-card-label">About the Author</p>
                  <a href={data.authorLinkedin} target="_blank" rel="noopener noreferrer" className="bdr-author-linkedin-link">
                    <h3 className="bdr-author-card-name">{data.author}</h3>
                  </a>
                  <p className="bdr-author-card-bio">
                    Co-Founder, guvrn.
                  </p>
                </div>
              </div>
            )}
          </article>

          {/* Right TOC sidebar */}
          {tocItems.length > 0 && (
            <aside className="bdr-toc">
              <div className="bdr-toc-inner">
                <p className="bdr-toc-label">On this page</p>
                <nav>
                  {tocItems.map(({ id, text, level }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className={`bdr-toc-link bdr-toc-h${level} ${activeId === id ? "bdr-toc-active" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    >
                      {text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}

        </div>

        {/* ── YOU MIGHT ALSO LIKE ── */}
        {popular.length > 0 && (
          <section className="bdr-related">
            <div className="bdr-related-header">
              <div className="bdr-related-label">
                <span className="bdr-related-line" />
                <span>Continue Reading</span>
                <span className="bdr-related-line" />
              </div>
              <h2 className="bdr-related-title">You Might Also Like</h2>
              <Link to="/guvrnspeak" className="bdr-view-all">
                View All Articles →
              </Link>
            </div>

            <div className="bdr-related-grid">
              {popular.map((blog, i) => (
                <Link
                  key={blog.slug}
                  to={`/guvrnspeak/${blog.slug}`}
                  className="bdr-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="bdr-card-image-wrap">
                    {blog.image ? (
                      <img src={blog.image} alt={blog.title} className="bdr-card-img" />
                    ) : (
                      <div className="bdr-card-img-placeholder" />
                    )}
                    <div className="bdr-card-hover-overlay">
                      <span className="bdr-card-read">Read Article</span>
                    </div>
                  </div>
                  <div className="bdr-card-body">
                    <div className="bdr-card-meta">
                      <span className="bdr-card-tag">Development</span>
                      <span className="bdr-card-date">{blog.date}</span>
                    </div>
                    <h3 className="bdr-card-title">{blog.title}</h3>
                    <span className="bdr-card-arrow">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </main>

      <Footer />
    </>
  );
}

export default BlogDetailPage;
